import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { LoggingService, LogLevel } from './logging.service';

export interface PayloadValidationConfig {
  maxBytes: number;
  maxChannelLength: number;
  rateLimitPerMinute: number;
}

export interface ValidationEvent {
  type: 'rejected' | 'rate_limited';
  reason: string;
  channel?: string;
  timestamp: number;
}

export interface ValidationResult {
  isValid: boolean;
  reason?: string;
  details?: any;
}

@Injectable({
  providedIn: 'root'
})
export class PayloadValidatorService {
  private config: PayloadValidationConfig = {
    maxBytes: 65536, // 64KB
    maxChannelLength: 200,
    rateLimitPerMinute: 100
  };

  // Rate limiting state per client/session
  private rateLimitData = {
    windowStart: 0,
    requestCount: 0
  };

  // Event emitter for validation events
  private validationEventsSubject = new Subject<ValidationEvent>();

  constructor(private loggingService: LoggingService) {}

  // Configure validation parameters
  configure(config: Partial<PayloadValidationConfig>): void {
    this.config = { ...this.config, ...config };
  }

  // Observable for validation events
  get validationEvents$() {
    return this.validationEventsSubject.asObservable();
  }

  // Validate payload size and structure
  validatePayload(data: any): ValidationResult {
    try {
      const serialized = JSON.stringify(data);
      const sizeBytes = new Blob([serialized]).size;

      if (sizeBytes > this.config.maxBytes) {
        const error = `Payload too large: ${sizeBytes} bytes (max: ${this.config.maxBytes})`;
        this.loggingService.log(LogLevel.WARN, 'PayloadValidatorService', error);
        
        this.validationEventsSubject.next({
          type: 'rejected',
          reason: 'payload_too_large',
          timestamp: Date.now()
        });

        return {
          isValid: false,
          reason: 'payload_too_large',
          details: { size: sizeBytes, limit: this.config.maxBytes }
        };
      }

      return { isValid: true };
    } catch (error) {
      this.loggingService.log(LogLevel.ERROR, 'PayloadValidatorService',
        'Failed to serialize payload', error);
      
      this.validationEventsSubject.next({
        type: 'rejected',
        reason: 'serialization_error',
        timestamp: Date.now()
      });

      return {
        isValid: false,
        reason: 'serialization_error',
        details: { error: error }
      };
    }
  }

  // Validate channel name
  validateChannel(channelName: string): ValidationResult {
    if (!channelName || typeof channelName !== 'string') {
      return {
        isValid: false,
        reason: 'invalid_channel_name',
        details: { provided: channelName }
      };
    }

    if (channelName.length > this.config.maxChannelLength) {
      this.validationEventsSubject.next({
        type: 'rejected',
        reason: 'channel_name_too_long',
        channel: channelName,
        timestamp: Date.now()
      });

      return {
        isValid: false,
        reason: 'channel_name_too_long',
        details: { length: channelName.length, limit: this.config.maxChannelLength }
      };
    }

    return { isValid: true };
  }

  // Check rate limiting (per client/session)
  checkRateLimit(): { allowed: boolean; status: any } {
    const now = Date.now();
    const windowDuration = 60000; // 1 minute in ms

    // Reset window if needed
    if (now - this.rateLimitData.windowStart > windowDuration) {
      this.rateLimitData.windowStart = now;
      this.rateLimitData.requestCount = 0;
    }

    this.rateLimitData.requestCount++;

    if (this.rateLimitData.requestCount > this.config.rateLimitPerMinute) {
      this.loggingService.log(LogLevel.WARN, 'PayloadValidatorService',
        `Rate limit exceeded: ${this.rateLimitData.requestCount}/${this.config.rateLimitPerMinute} per minute`);

      this.validationEventsSubject.next({
        type: 'rate_limited',
        reason: 'Rate limit exceeded',
        timestamp: now
      });

      return {
        allowed: false,
        status: {
          count: this.rateLimitData.requestCount,
          limit: this.config.rateLimitPerMinute,
          windowStart: this.rateLimitData.windowStart
        }
      };
    }

    return {
      allowed: true,
      status: {
        count: this.rateLimitData.requestCount,
        limit: this.config.rateLimitPerMinute,
        windowStart: this.rateLimitData.windowStart
      }
    };
  }

  // Sanitize channel name
  sanitizeChannelName(channelName: string): string {
    if (!channelName || typeof channelName !== 'string') {
      return '';
    }

    // Remove potentially dangerous characters and limit length
    const sanitized = channelName
      .replace(/[<>\"'&]/g, '') // Remove HTML/script dangerous chars
      .trim()
      .substring(0, this.config.maxChannelLength);

    return sanitized;
  }

  // Get current rate limit status
  getRateLimitStatus() {
    return {
      count: this.rateLimitData.requestCount,
      limit: this.config.rateLimitPerMinute,
      windowStart: this.rateLimitData.windowStart,
      timeRemaining: Math.max(0, 60000 - (Date.now() - this.rateLimitData.windowStart))
    };
  }

  // Reset rate limit (for testing)
  resetRateLimit(): void {
    this.rateLimitData.requestCount = 0;
    this.rateLimitData.windowStart = Date.now();
  }

  // Cleanup resources
  destroy(): void {
    this.validationEventsSubject.complete();
  }
}
